// RiskLayout — persistent two-panel layout for all risk routes.
// Left panel: register tree (always visible, state persisted across navigation).
// Right panel: child route via Outlet (list or detail).
//
// Selected register is stored in context so both the list view and detail view
// can access it. When navigating to a risk detail, the tree selection is
// preserved; clicking a different register navigates back to the list.

import { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react'
import { Outlet, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { registersApi } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { RegisterTree } from '@/components/features/register-tree'
import { ResizablePanel } from '@/components/shared/ResizablePanel'

// --- Register selection context ---

interface RegisterSelection {
  registerId: string | null
  registerName: string
}

const RegisterSelectionContext = createContext<RegisterSelection>({
  registerId: null,
  registerName: '',
})

/** Access the currently selected register from any child route. */
export function useSelectedRegister() {
  return useContext(RegisterSelectionContext)
}

// --- Layout component ---

export function RiskLayout() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const api = useApi()
  const [selected, setSelected] = useState<RegisterSelection>({
    registerId: null,
    registerName: '',
  })

  // Query tree data at layout level for auto-selection of root node
  const { data: registers } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => registersApi(api).tree(),
  })

  // Auto-select root node on first load
  const hasAutoSelected = useRef(false)
  useEffect(() => {
    if (registers && registers.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true
      const root = registers.find((r) => r.depth === 0) ?? registers[0]
      if (root) setSelected({ registerId: root.id, registerName: root.name })
    }
  }, [registers])

  const handleSelectRegister = useCallback(
    (id: string, name: string) => {
      setSelected({ registerId: id, registerName: name })
      // Navigate to the risk list for this register (clearing any detail view)
      navigate({ to: `/t/${slug}/risks` })
    },
    [navigate, slug],
  )

  return (
    <RegisterSelectionContext.Provider value={selected}>
      <ResizablePanel
        className="h-[calc(100vh-8rem)]"
        left={
          <div className="flex h-full flex-col">
            <div className="border-b p-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Registers
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              <RegisterTree
                selectedRegisterId={selected.registerId}
                onSelectRegister={handleSelectRegister}
              />
            </div>
          </div>
        }
        right={
          <div className="flex h-full flex-col overflow-hidden">
            <Outlet />
          </div>
        }
      />
    </RegisterSelectionContext.Provider>
  )
}
